import { useMemo, useState, type FormEvent } from 'react';
import { AlertTriangle, ArrowRight, Check, EyeOff, Minus, Plus, Trash2 } from 'lucide-react';

import {
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  Drawer,
  EmptyState,
  ErrorAlert,
  Field,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Table,
  Td,
  Textarea,
  Th,
} from '@/components/ui';
import {
  ENTITY_LABELS,
  useDeleteRedirect,
  useDeleteSeo,
  useRedirects,
  useSaveRedirect,
  useSaveSeo,
  useSeoDashboard,
  useSeoRecords,
  type SeoEntityType,
  type SeoFilter,
  type SeoRecord,
  type SeoRedirect,
  type SeoSchemaType,
} from '@/lib/seo-api';
import { DESCRIPTION_RANGE, TITLE_RANGE, truncate } from '@/lib/product-seo';
import { scoreSeo, type SeoCheck } from '@/lib/seo-score';
import { siteOrigin } from '@/lib/env';
import { cn, errorMessage, fieldErrors, formatDateTime, formatNumber } from '@/lib/utils';

/**
 * SEO management (SEO master prompt §4, §26, §36).
 *
 * Three tabs rather than the nine screens the brief sketches, because the
 * underlying table is one table: "Products SEO" and "Categories SEO" would be
 * the same list with a filter pre-applied, and a filter is what the records
 * tab already has. Splitting them would mean six screens to keep in step and
 * six places to fix a bug in the editor.
 */

type Tab = 'dashboard' | 'records' | 'redirects';

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'records', label: 'Pages & records' },
  { id: 'redirects', label: 'Redirects' },
];

export function SeoPage() {
  const [tab, setTab] = useState<Tab>('dashboard');

  return (
    <div className="space-y-5">
      <PageHeader
        title="SEO"
        description="Search and social metadata for every page, with redirects and a site-wide audit."
      />

      <div className="flex gap-1 border-b border-line" role="tablist">
        {TABS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            role="tab"
            aria-selected={tab === entry.id}
            onClick={() => setTab(entry.id)}
            className={cn(
              '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              tab === entry.id
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-ink-muted hover:text-ink',
            )}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && <DashboardTab onOpenRecords={() => setTab('records')} />}
      {tab === 'records' && <RecordsTab />}
      {tab === 'redirects' && <RedirectsTab />}
    </div>
  );
}

/* ==========================================================================
   Dashboard
   ========================================================================== */

function StatTile({
  label,
  value,
  tone = 'neutral',
  hint,
}: {
  label: string;
  value: string | number;
  tone?: 'neutral' | 'warning' | 'danger';
  hint?: string;
}) {
  return (
    <Card className="p-4">
      <p className="text-xs font-medium text-ink-subtle">{label}</p>
      <p
        className={cn(
          'mt-1 text-2xl font-bold tabular-nums',
          tone === 'danger' ? 'text-danger-600' : tone === 'warning' ? 'text-warning-700' : 'text-ink',
        )}
      >
        {value}
      </p>
      {hint && <p className="mt-1 text-[11px] text-ink-subtle">{hint}</p>}
    </Card>
  );
}

function DashboardTab({ onOpenRecords }: { onOpenRecords: () => void }) {
  const dashboard = useSeoDashboard();

  if (dashboard.isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }

  if (dashboard.isError || !dashboard.data) {
    return <ErrorAlert title="Could not load the SEO dashboard">{errorMessage(dashboard.error)}</ErrorAlert>;
  }

  const data = dashboard.data;

  return (
    <div className="space-y-5">
      {/* "Missing" here means no override, not a broken page — the resolver
          gives every page a title. Said out loud because a screen full of
          "missing" that actually means "untouched" reads as an outage. */}
      <p className="text-xs text-ink-subtle">
        Every page renders a title and description whether or not it appears below — these counts
        are pages nobody has written search copy for, not pages that are broken.
      </p>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label="Pages with custom SEO" value={formatNumber(data.totalConfigured)} />
        <StatTile
          label="Excluded from search"
          value={formatNumber(data.noIndexCount)}
          tone={data.noIndexCount > 0 ? 'warning' : 'neutral'}
          hint="noindex — deliberate for a discontinued line, a mistake anywhere else"
        />
        <StatTile
          label="Duplicate titles"
          value={formatNumber(data.duplicateTitles.length)}
          tone={data.duplicateTitles.length > 0 ? 'danger' : 'neutral'}
          hint="Two pages competing for one query"
        />
        <StatTile
          label="Redirects"
          value={`${formatNumber(data.redirects.active)} / ${formatNumber(data.redirects.total)}`}
          hint={`${formatNumber(data.redirects.unused)} never hit`}
        />
      </div>

      <Card>
        <CardHeader title="Coverage" subtitle="How many pages of each kind carry their own SEO." />
        <div className="space-y-3 p-4 pt-0">
          {data.coverage.map((row) => {
            const percent = row.total > 0 ? Math.round((row.configured / row.total) * 100) : 0;

            return (
              <div key={row.entityType}>
                <div className="mb-1 flex items-baseline justify-between text-sm">
                  <span className="font-medium text-ink">
                    {ENTITY_LABELS[row.entityType as SeoEntityType] ?? row.entityType}
                  </span>
                  <span className="tabular-nums text-xs text-ink-muted">
                    {formatNumber(row.configured)} of {formatNumber(row.total)}
                  </span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-canvas">
                  <div
                    className={cn(
                      'h-full rounded-full',
                      percent >= 80 ? 'bg-success-500' : percent >= 40 ? 'bg-warning-500' : 'bg-danger-500',
                    )}
                    style={{ width: `${percent}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader
            title="Gaps"
            subtitle="Records that exist but are not finished."
            action={
              <Button type="button" variant="ghost" size="sm" onClick={onOpenRecords}>
                Open records
                <ArrowRight className="size-3.5" aria-hidden="true" />
              </Button>
            }
          />
          <div className="space-y-2 p-4 pt-0 text-sm">
            <GapRow label="No meta title" value={data.missingTitle} />
            <GapRow label="No meta description" value={data.missingDescription} />
            <GapRow label="No social image" value={data.missingOgImage} />
            <GapRow label="No focus keyword" value={data.missingFocusKeyword} />
          </div>
        </Card>

        <Card>
          <CardHeader
            title="Duplicates"
            subtitle="The same copy on more than one page splits their ranking."
          />
          <div className="space-y-2 p-4 pt-0 text-sm">
            {data.duplicateTitles.length === 0 && data.duplicateDescriptions.length === 0 ? (
              <p className="text-ink-subtle">No duplicate titles or descriptions.</p>
            ) : (
              <>
                {data.duplicateTitles.map((row) => (
                  <div key={row.title} className="flex items-start justify-between gap-3">
                    <span className="min-w-0 truncate text-ink-muted">{row.title}</span>
                    <Badge tone="danger">{row.count} pages</Badge>
                  </div>
                ))}
                {data.duplicateDescriptions.map((row) => (
                  <div key={row.description} className="flex items-start justify-between gap-3">
                    <span className="min-w-0 truncate text-ink-subtle">
                      {truncate(row.description, 60)}
                    </span>
                    <Badge tone="warning">{row.count} descriptions</Badge>
                  </div>
                ))}
              </>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

function GapRow({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-ink-muted">{label}</span>
      <span className={cn('tabular-nums font-medium', value > 0 ? 'text-warning-700' : 'text-ink-subtle')}>
        {formatNumber(value)}
      </span>
    </div>
  );
}

/* ==========================================================================
   Records
   ========================================================================== */

const FILTERS: Array<{ id: SeoFilter; label: string }> = [
  { id: 'ALL', label: 'All records' },
  { id: 'MISSING_TITLE', label: 'Missing title' },
  { id: 'MISSING_DESCRIPTION', label: 'Missing description' },
  { id: 'MISSING_OG_IMAGE', label: 'Missing social image' },
  { id: 'NOINDEX', label: 'Excluded from search' },
  { id: 'DUPLICATE_TITLE', label: 'Duplicate title' },
];

const ENTITY_TYPES: SeoEntityType[] = [
  'HOME',
  'ROUTE',
  'PAGE',
  'PRODUCT',
  'CATEGORY',
  'BRAND',
  'BLOG',
  'LANDING_PAGE',
  'LOCATION',
];

function RecordsTab() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<SeoFilter>('ALL');
  const [entityType, setEntityType] = useState<SeoEntityType | ''>('');
  const [editing, setEditing] = useState<SeoRecord | 'new' | null>(null);

  const records = useSeoRecords({ search, filter, entityType });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Search" htmlFor="seo-search" className="min-w-56 flex-1">
          <Input
            id="seo-search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Title, focus keyword or entity id…"
          />
        </Field>

        <Field label="Entity" htmlFor="seo-entity">
          <Select
            id="seo-entity"
            value={entityType}
            onChange={(event) => setEntityType(event.target.value as SeoEntityType | '')}
          >
            <option value="">All entities</option>
            {ENTITY_TYPES.map((type) => (
              <option key={type} value={type}>
                {ENTITY_LABELS[type]}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Status" htmlFor="seo-filter">
          <Select
            id="seo-filter"
            value={filter}
            onChange={(event) => setFilter(event.target.value as SeoFilter)}
          >
            {FILTERS.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.label}
              </option>
            ))}
          </Select>
        </Field>

        <Button type="button" onClick={() => setEditing('new')}>
          <Plus className="size-4" aria-hidden="true" />
          New record
        </Button>
      </div>

      {records.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : records.isError ? (
        <ErrorAlert title="Could not load SEO records">{errorMessage(records.error)}</ErrorAlert>
      ) : records.data && records.data.items.length === 0 ? (
        <EmptyState
          title="No records match"
          description="Pages without a record still render a derived title and description."
        />
      ) : (
        <Card>
          <Table>
            <thead>
              <tr>
                <Th>Entity</Th>
                <Th>Meta title</Th>
                <Th>Focus keyword</Th>
                <Th>Robots</Th>
                <Th>Updated</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {records.data?.items.map((record) => (
                <tr key={record.id} className="border-t border-line">
                  <Td>
                    <span className="font-medium text-ink">{ENTITY_LABELS[record.entityType]}</span>
                    <span className="block truncate text-[11px] text-ink-subtle">
                      {record.entityId}
                    </span>
                  </Td>
                  <Td>
                    <span className={cn(!record.metaTitle && 'text-ink-subtle')}>
                      {record.metaTitle ?? 'Derived from the page'}
                    </span>
                  </Td>
                  <Td>{record.focusKeyword ?? <span className="text-ink-subtle">—</span>}</Td>
                  <Td>
                    {record.robotsIndex ? (
                      <Badge tone="success">Index</Badge>
                    ) : (
                      <Badge tone="warning">
                        <EyeOff className="size-3" aria-hidden="true" />
                        Noindex
                      </Badge>
                    )}
                  </Td>
                  <Td>
                    <span className="text-xs text-ink-subtle">
                      {formatDateTime(record.updatedAt)}
                      {record.updatedByName && ` · ${record.updatedByName}`}
                    </span>
                  </Td>
                  <Td>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(record)}>
                      Edit
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {editing && (
        <SeoEditorDrawer
          record={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/* ==========================================================================
   Editor
   ========================================================================== */

interface EditorState {
  entityType: SeoEntityType;
  entityId: string;
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
  focusKeyword: string;
  secondaryKeywords: string;
  canonicalUrl: string;
  robotsIndex: boolean;
  robotsFollow: boolean;
  ogTitle: string;
  ogDescription: string;
  ogImageUrl: string;
  schemaType: SeoSchemaType;
  schemaJson: string;
}

const SCHEMA_TYPES: SeoSchemaType[] = [
  'NONE',
  'WEBSITE',
  'ORGANIZATION',
  'PRODUCT',
  'ARTICLE',
  'BREADCRUMB_LIST',
  'FAQ_PAGE',
  'ITEM_LIST',
  'LOCAL_BUSINESS',
  'COLLECTION_PAGE',
];

function toEditorState(record: SeoRecord | null): EditorState {
  return {
    entityType: record?.entityType ?? 'PAGE',
    entityId: record?.entityId ?? '',
    metaTitle: record?.metaTitle ?? '',
    metaDescription: record?.metaDescription ?? '',
    metaKeywords: record?.metaKeywords ?? '',
    focusKeyword: record?.focusKeyword ?? '',
    secondaryKeywords: record?.secondaryKeywords ?? '',
    canonicalUrl: record?.canonicalUrl ?? '',
    robotsIndex: record?.robotsIndex ?? true,
    robotsFollow: record?.robotsFollow ?? true,
    ogTitle: record?.ogTitle ?? '',
    ogDescription: record?.ogDescription ?? '',
    ogImageUrl: record?.ogImageUrl ?? '',
    schemaType: record?.schemaType ?? 'NONE',
    schemaJson: record?.schemaJson ?? '',
  };
}

function CharacterCount({ value, min, ideal }: { value: string; min: number; ideal: number }) {
  const { length } = value;
  const tone =
    length === 0
      ? 'text-ink-subtle'
      : length > ideal
        ? 'text-danger-600'
        : length < min
          ? 'text-warning-700'
          : 'text-success-600';

  return (
    <span className={cn('tabular-nums text-[11px]', tone)}>
      {length} / {ideal}
    </span>
  );
}

function CheckRow({ check }: { check: SeoCheck }) {
  const Icon = check.level === 'good' ? Check : check.level === 'warning' ? AlertTriangle : Minus;

  return (
    <li
      className={cn(
        'flex items-start gap-1.5',
        check.level === 'good'
          ? 'text-success-700'
          : check.level === 'warning'
            ? 'text-warning-700'
            : 'text-ink-subtle',
      )}
    >
      <Icon className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
      <span>
        {check.label}
        {check.detail && <span className="block text-[11px] text-ink-subtle">{check.detail}</span>}
      </span>
    </li>
  );
}

function SeoEditorDrawer({ record, onClose }: { record: SeoRecord | null; onClose: () => void }) {
  const save = useSaveSeo();
  const remove = useDeleteSeo();
  const [form, setForm] = useState<EditorState>(() => toEditorState(record));

  const errors = fieldErrors(save.error);
  const update = <K extends keyof EditorState>(key: K, value: EditorState[K]): void =>
    setForm((previous) => ({ ...previous, [key]: value }));

  // The score measures what will actually render, so it needs the resolved
  // values — an empty meta title still produces a title on the page.
  const effectiveTitle = form.metaTitle.trim() || '(derived from the page)';
  const effectiveDescription = form.metaDescription.trim() || '(derived from the page)';

  const score = useMemo(
    () =>
      scoreSeo({
        title: effectiveTitle,
        description: effectiveDescription,
        titleIsCustom: form.metaTitle.trim() !== '',
        descriptionIsCustom: form.metaDescription.trim() !== '',
        focusKeyword: form.focusKeyword.trim() || null,
        canonicalUrl: form.canonicalUrl.trim() || null,
        ogImageUrl: form.ogImageUrl.trim() || null,
        robotsIndex: form.robotsIndex,
        hasSchema: form.schemaType !== 'NONE' || form.schemaJson.trim() !== '',
      }),
    [form, effectiveTitle, effectiveDescription],
  );

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();

    save.mutate(
      {
        entityType: form.entityType,
        entityId: form.entityId.trim() || undefined,
        metaTitle: form.metaTitle.trim() || null,
        metaDescription: form.metaDescription.trim() || null,
        metaKeywords: form.metaKeywords.trim() || null,
        focusKeyword: form.focusKeyword.trim() || null,
        secondaryKeywords: form.secondaryKeywords.trim() || null,
        canonicalUrl: form.canonicalUrl.trim() || null,
        robotsIndex: form.robotsIndex,
        robotsFollow: form.robotsFollow,
        ogTitle: form.ogTitle.trim() || null,
        ogDescription: form.ogDescription.trim() || null,
        ogImageUrl: form.ogImageUrl.trim() || null,
        schemaType: form.schemaType,
        schemaJson: form.schemaJson.trim() || null,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={record ? 'Edit SEO' : 'New SEO record'}
      footer={
        <div className="flex items-center justify-between gap-2">
          {record ? (
            <Button
              type="button"
              variant="danger"
              size="sm"
              disabled={remove.isPending}
              onClick={() => remove.mutate(record.id, { onSuccess: onClose })}
            >
              <Trash2 className="size-3.5" aria-hidden="true" />
              Clear overrides
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" form="seo-form" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      }
    >
      <form id="seo-form" onSubmit={handleSubmit} className="space-y-5">
        {save.isError && <ErrorAlert title="Could not save">{errorMessage(save.error)}</ErrorAlert>}
        {remove.isError && (
          <ErrorAlert title="Could not clear this record">{errorMessage(remove.error)}</ErrorAlert>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Entity type" htmlFor="entityType" required>
            <Select
              id="entityType"
              value={form.entityType}
              disabled={record !== null}
              onChange={(event) => update('entityType', event.target.value as SeoEntityType)}
            >
              {ENTITY_TYPES.map((type) => (
                <option key={type} value={type}>
                  {ENTITY_LABELS[type]}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label="Entity id or path"
            htmlFor="entityId"
            hint={form.entityType === 'HOME' ? 'Leave blank — there is one homepage.' : undefined}
            error={errors['entityId']}
          >
            <Input
              id="entityId"
              value={form.entityId}
              disabled={record !== null || form.entityType === 'HOME'}
              onChange={(event) => update('entityId', event.target.value)}
              placeholder={form.entityType === 'ROUTE' ? '/products' : 'uuid'}
            />
          </Field>
        </div>

        {/* Google preview, resolved the same way the storefront resolves it. */}
        <div className="space-y-1 rounded-lg border border-line bg-canvas px-3 py-3">
          <p className="truncate text-[11px] text-success-700">
            {form.canonicalUrl.trim() || `${siteOrigin}/…`}
          </p>
          <p className="truncate text-sm font-medium text-brand-700">
            {truncate(effectiveTitle, 65)}
          </p>
          <p className="text-xs leading-relaxed text-ink-muted">
            {truncate(effectiveDescription, 160)}
          </p>
        </div>

        <Field label="Meta title" htmlFor="metaTitle" error={errors['metaTitle']}>
          <Input
            id="metaTitle"
            value={form.metaTitle}
            maxLength={180}
            onChange={(event) => update('metaTitle', event.target.value)}
          />
        </Field>
        <div className="-mt-2 flex justify-end">
          <CharacterCount value={form.metaTitle} min={TITLE_RANGE.min} ideal={TITLE_RANGE.ideal} />
        </div>

        <Field label="Meta description" htmlFor="metaDescription" error={errors['metaDescription']}>
          <Textarea
            id="metaDescription"
            rows={3}
            maxLength={400}
            value={form.metaDescription}
            onChange={(event) => update('metaDescription', event.target.value)}
          />
        </Field>
        <div className="-mt-2 flex justify-end">
          <CharacterCount
            value={form.metaDescription}
            min={DESCRIPTION_RANGE.min}
            ideal={DESCRIPTION_RANGE.ideal}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Focus keyword" htmlFor="focusKeyword" hint="Editorial — never rendered.">
            <Input
              id="focusKeyword"
              value={form.focusKeyword}
              maxLength={120}
              onChange={(event) => update('focusKeyword', event.target.value)}
            />
          </Field>
          <Field label="Secondary keywords" htmlFor="secondaryKeywords" hint="Comma separated.">
            <Input
              id="secondaryKeywords"
              value={form.secondaryKeywords}
              maxLength={500}
              onChange={(event) => update('secondaryKeywords', event.target.value)}
            />
          </Field>
        </div>

        <Field label="Meta keywords" htmlFor="metaKeywords" hint="Comma separated.">
          <Input
            id="metaKeywords"
            value={form.metaKeywords}
            maxLength={500}
            onChange={(event) => update('metaKeywords', event.target.value)}
          />
        </Field>

        <Field
          label="Canonical URL"
          htmlFor="canonicalUrl"
          hint="Only when this page duplicates another. Blank uses its own URL."
          error={errors['canonicalUrl']}
        >
          <Input
            id="canonicalUrl"
            type="url"
            value={form.canonicalUrl}
            maxLength={500}
            onChange={(event) => update('canonicalUrl', event.target.value)}
          />
        </Field>

        <section className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">Indexing</h3>
          <Checkbox
            label="Index — allow this page in search results and the sitemap"
            checked={form.robotsIndex}
            onChange={(event) => update('robotsIndex', event.target.checked)}
          />
          <Checkbox
            label="Follow — pass ranking credit to links on this page"
            checked={form.robotsFollow}
            onChange={(event) => update('robotsFollow', event.target.checked)}
          />
          {/* Shown before saving, not after: this is the one setting here that
              can quietly remove a page from search. */}
          {!form.robotsIndex && (
            <div className="flex items-start gap-2 rounded-lg bg-warning-50 px-3 py-2 text-xs text-warning-700">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
              This page will be removed from search results and dropped from the sitemap.
            </div>
          )}
        </section>

        <section className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">
            Social card
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Social title" htmlFor="ogTitle">
              <Input
                id="ogTitle"
                value={form.ogTitle}
                maxLength={180}
                onChange={(event) => update('ogTitle', event.target.value)}
                placeholder={truncate(effectiveTitle, 40)}
              />
            </Field>
            <Field label="Social image URL" htmlFor="ogImageUrl" error={errors['ogImageUrl']}>
              <Input
                id="ogImageUrl"
                value={form.ogImageUrl}
                maxLength={500}
                onChange={(event) => update('ogImageUrl', event.target.value)}
                placeholder="Falls back to the page image"
              />
            </Field>
          </div>
          <Field label="Social description" htmlFor="ogDescription">
            <Textarea
              id="ogDescription"
              rows={2}
              maxLength={400}
              value={form.ogDescription}
              onChange={(event) => update('ogDescription', event.target.value)}
              placeholder={truncate(effectiveDescription, 60)}
            />
          </Field>
        </section>

        <section className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">
            Structured data
          </h3>
          <Field label="Schema type" htmlFor="schemaType">
            <Select
              id="schemaType"
              value={form.schemaType}
              onChange={(event) => update('schemaType', event.target.value as SeoSchemaType)}
            >
              {SCHEMA_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type === 'NONE' ? 'None' : type}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label="Custom JSON-LD"
            htmlFor="schemaJson"
            hint="Replaces the generated block. Only describe what is actually on the page."
            error={errors['schemaJson']}
          >
            <Textarea
              id="schemaJson"
              rows={4}
              className="font-mono text-xs"
              value={form.schemaJson}
              onChange={(event) => update('schemaJson', event.target.value)}
            />
          </Field>
        </section>

        <section className="space-y-2 rounded-lg border border-line p-3">
          <div className="flex items-baseline justify-between">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">
              SEO score
            </h3>
            <span
              className={cn(
                'text-lg font-bold tabular-nums',
                score.score >= 80
                  ? 'text-success-600'
                  : score.score >= 50
                    ? 'text-warning-700'
                    : 'text-danger-600',
              )}
            >
              {score.score} / 100
            </span>
          </div>
          {/* Advisory. Nothing here blocks a save — a merchandiser who wants a
              70-character title has a reason, and the tool does not overrule
              the person using it. */}
          <ul className="space-y-1 text-xs">
            {score.checks.map((check) => (
              <CheckRow key={check.id} check={check} />
            ))}
          </ul>
        </section>
      </form>
    </Drawer>
  );
}

/* ==========================================================================
   Redirects
   ========================================================================== */

function RedirectsTab() {
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<SeoRedirect | 'new' | null>(null);
  const redirects = useRedirects(search);
  const remove = useDeleteRedirect();

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Search" htmlFor="redirect-search" className="min-w-56 flex-1">
          <Input
            id="redirect-search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="/old-path"
          />
        </Field>
        <Button type="button" onClick={() => setEditing('new')}>
          <Plus className="size-4" aria-hidden="true" />
          New redirect
        </Button>
      </div>

      {remove.isError && (
        <ErrorAlert title="Could not delete this redirect">{errorMessage(remove.error)}</ErrorAlert>
      )}

      {redirects.isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : redirects.data && redirects.data.items.length === 0 ? (
        <EmptyState
          title="No redirects"
          description="Add one when a URL changes, so its inbound links and ranking survive the move."
        />
      ) : (
        <Card>
          <Table>
            <thead>
              <tr>
                <Th>From</Th>
                <Th>To</Th>
                <Th>Status</Th>
                <Th>Hits</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {redirects.data?.items.map((row) => (
                <tr key={row.id} className="border-t border-line">
                  <Td>
                    <span className="font-mono text-xs">{row.fromPath}</span>
                  </Td>
                  <Td>
                    <span className="font-mono text-xs">{row.toPath}</span>
                  </Td>
                  <Td>
                    <Badge tone={row.status === 'PERMANENT' ? 'brand' : 'neutral'}>
                      {row.status === 'PERMANENT' ? '301' : '302'}
                    </Badge>
                    {!row.isActive && (
                      <Badge tone="neutral" className="ml-1">
                        Off
                      </Badge>
                    )}
                  </Td>
                  <Td>
                    <span className="tabular-nums text-xs text-ink-muted">
                      {formatNumber(row.hitCount)}
                    </span>
                  </Td>
                  <Td>
                    <div className="flex justify-end gap-1">
                      <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(row)}>
                        Edit
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => remove.mutate(row.id)}
                      >
                        <Trash2 className="size-3.5 text-danger-600" aria-hidden="true" />
                        <span className="sr-only">Delete {row.fromPath}</span>
                      </Button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {editing && (
        <RedirectDrawer
          redirect={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function RedirectDrawer({
  redirect,
  onClose,
}: {
  redirect: SeoRedirect | null;
  onClose: () => void;
}) {
  const save = useSaveRedirect();
  const [fromPath, setFromPath] = useState(redirect?.fromPath ?? '');
  const [toPath, setToPath] = useState(redirect?.toPath ?? '');
  const [permanent, setPermanent] = useState(redirect ? redirect.status === 'PERMANENT' : true);
  const [isActive, setIsActive] = useState(redirect?.isActive ?? true);
  const [notes, setNotes] = useState(redirect?.notes ?? '');

  const errors = fieldErrors(save.error);

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();

    save.mutate(
      {
        id: redirect?.id ?? null,
        body: {
          fromPath: fromPath.trim(),
          toPath: toPath.trim(),
          status: permanent ? 'PERMANENT' : 'TEMPORARY',
          isActive,
          notes: notes.trim() || null,
        },
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={redirect ? 'Edit redirect' : 'New redirect'}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="redirect-form" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      }
    >
      <form id="redirect-form" onSubmit={handleSubmit} className="space-y-4">
        {/* Loops, self-redirects and over-long chains are refused by the server
            with a message naming the path involved — surfaced verbatim here,
            because "400" tells an admin nothing about which rule collides. */}
        {save.isError && (
          <ErrorAlert title="Could not save this redirect">{errorMessage(save.error)}</ErrorAlert>
        )}

        <Field
          label="From"
          htmlFor="fromPath"
          required
          hint="Root-relative, e.g. /corporate-gift"
          error={errors['fromPath']}
        >
          <Input
            id="fromPath"
            value={fromPath}
            required
            onChange={(event) => setFromPath(event.target.value)}
            placeholder="/corporate-gift"
          />
        </Field>

        <Field
          label="To"
          htmlFor="toPath"
          required
          hint="A path on this site, or a full URL to leave it."
          error={errors['toPath']}
        >
          <Input
            id="toPath"
            value={toPath}
            required
            onChange={(event) => setToPath(event.target.value)}
            placeholder="/corporate-gifts"
          />
        </Field>

        <Checkbox
          label="Permanent (301) — tells search engines to move the ranking across"
          checked={permanent}
          onChange={(event) => setPermanent(event.target.checked)}
        />
        <Checkbox
          label="Active"
          checked={isActive}
          onChange={(event) => setIsActive(event.target.checked)}
        />

        <Field label="Notes" htmlFor="notes" hint="Why this rule exists — for whoever finds it later.">
          <Textarea
            id="notes"
            rows={2}
            maxLength={500}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </Field>
      </form>
    </Drawer>
  );
}
